/**
 * LYT Communications - Damage Report
 * Version: 1.0.0
 * Created: 2026-02-23
 *
 * Emergency documentation when a utility is hit during boring.
 * Captures utility type, severity, description, GPS, and photos.
 * Available from BoringTracker during "In Progress" status.
 */

import React, { useState, useCallback } from 'react';
import { AlertTriangle, Camera, MapPin, Send, X, ChevronDown, ChevronUp } from 'lucide-react';
import { isGpsEnabled } from '../../services/gpsService';

const UTILITY_TYPES = ['Electric', 'Gas', 'Water', 'Sewer', 'Telecom', 'Cable', 'Fiber', 'Storm Drain', 'Other'];
const SEVERITY_LEVELS = [
  { id: 'minor', label: 'Minor', color: '#FFB800', description: 'Small nick or scrape, no service interruption' },
  { id: 'moderate', label: 'Moderate', color: '#FF9800', description: 'Damaged but still functional, needs repair' },
  { id: 'major', label: 'Major', color: '#FF4444', description: 'Service interrupted, immediate repair required' },
  { id: 'critical', label: 'Critical', color: '#D32F2F', description: 'Safety hazard — gas leak, live electric, water main' },
];

function DamageReport({ segment, darkMode, user, onSubmit, existingReports = [] }) {
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    utility_type: '',
    severity: '',
    description: '',
    gps_lat: '',
    gps_lng: '',
  });

  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const inputBg = darkMode ? '#0d1b2a' : '#ffffff';

  const segReports = existingReports.filter(r => r.segment_id === segment?.segment_id);

  const captureGPS = useCallback(() => {
    if (navigator.geolocation && isGpsEnabled()) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setForm(prev => ({
            ...prev,
            gps_lat: pos.coords.latitude.toFixed(6),
            gps_lng: pos.coords.longitude.toFixed(6),
          }));
        },
        () => { /* GPS failed — user can enter manually */ }
      );
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!form.utility_type || !form.severity || !form.description.trim()) return;
    setSubmitting(true);
    await onSubmit?.({
      project_id: segment?.project_id || '',
      segment_id: segment?.segment_id || '',
      reported_by: user?.name || 'Unknown',
      reported_by_email: user?.email || '',
      utility_type: form.utility_type,
      severity: form.severity,
      description: form.description.trim(),
      gps_lat: form.gps_lat,
      gps_lng: form.gps_lng,
      photos: '',
    });
    setForm({ utility_type: '', severity: '', description: '', gps_lat: '', gps_lng: '' });
    setShowForm(false);
    setSubmitting(false);
  }, [form, segment, user, onSubmit]);

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: `1px solid ${borderColor}`, background: inputBg, color: text,
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{
      background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10,
      overflow: 'hidden', marginBottom: 8,
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', cursor: 'pointer',
          borderBottom: expanded ? `1px solid ${borderColor}` : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} color="#FF4444" />
          <span style={{ fontWeight: 700, fontSize: 14, color: text }}>Damage Reports</span>
          {segReports.length > 0 && (
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
              background: '#FF444420', color: '#FF4444',
            }}>
              {segReports.length} report{segReports.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={14} color={textMuted} /> : <ChevronDown size={14} color={textMuted} />}
      </div>

      {expanded && (
        <div style={{ padding: '10px 14px' }}>
          {/* Existing reports */}
          {segReports.map((report, idx) => {
            const sev = SEVERITY_LEVELS.find(s => s.id === report.severity) || {};
            return (
              <div key={report.report_id || idx} style={{
                padding: '10px 12px', borderRadius: 8,
                border: `1px solid ${sev.color || borderColor}40`, marginBottom: 6,
                background: darkMode ? '#0d1b2a' : '#fff',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: text }}>{report.report_id}</span>
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 8, fontWeight: 600,
                      background: `${sev.color || '#9E9E9E'}20`, color: sev.color || '#9E9E9E',
                    }}>{report.severity?.toUpperCase()}</span>
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 8,
                      background: darkMode ? '#1e3a5f' : '#e2e8f0', color: textMuted,
                    }}>{report.utility_type}</span>
                  </div>
                  <span style={{
                    fontSize: 10, padding: '2px 6px', borderRadius: 8, fontWeight: 600,
                    background: report.status === 'Open' ? '#FF444420' : '#4CAF5020',
                    color: report.status === 'Open' ? '#FF4444' : '#4CAF50',
                  }}>{report.status}</span>
                </div>
                <div style={{ fontSize: 12, color: textMuted }}>{report.description}</div>
                <div style={{ fontSize: 10, color: textMuted, marginTop: 4 }}>
                  Reported by {report.reported_by} — {report.timestamp ? new Date(report.timestamp).toLocaleString() : '—'}
                </div>
              </div>
            );
          })}

          {/* New report form */}
          {showForm ? (
            <div style={{
              padding: 12, borderRadius: 8, border: `1px solid #FF444440`,
              background: darkMode ? '#0d1b2a' : '#fff8f8', marginTop: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#FF4444' }}>Report Utility Damage</span>
                <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                  <X size={14} color={textMuted} />
                </button>
              </div>

              {/* Utility Type */}
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Utility Type *</label>
                <select value={form.utility_type} onChange={e => setForm({ ...form, utility_type: e.target.value })} style={inputStyle}>
                  <option value="">Select utility type...</option>
                  {UTILITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {/* Severity */}
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Severity *</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {SEVERITY_LEVELS.map(sev => (
                    <button
                      key={sev.id}
                      type="button"
                      onClick={() => setForm({ ...form, severity: sev.id })}
                      style={{
                        padding: '8px', borderRadius: 6, textAlign: 'left', cursor: 'pointer',
                        border: `2px solid ${form.severity === sev.id ? sev.color : borderColor}`,
                        background: form.severity === sev.id ? `${sev.color}15` : 'transparent',
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 700, color: sev.color }}>{sev.label}</div>
                      <div style={{ fontSize: 10, color: textMuted }}>{sev.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Description *</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe what happened, what was hit, current condition..."
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>

              {/* GPS */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>GPS Lat</label>
                  <input value={form.gps_lat} onChange={e => setForm({ ...form, gps_lat: e.target.value })} placeholder="Auto or manual" style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>GPS Lng</label>
                  <input value={form.gps_lng} onChange={e => setForm({ ...form, gps_lng: e.target.value })} placeholder="Auto or manual" style={inputStyle} />
                </div>
                <button
                  type="button"
                  onClick={captureGPS}
                  style={{
                    alignSelf: 'flex-end', padding: '8px 10px', borderRadius: 6,
                    background: darkMode ? '#1e3a5f' : '#e2e8f0', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                    color: text, fontSize: 11,
                  }}
                >
                  <MapPin size={12} /> GPS
                </button>
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={submitting || !form.utility_type || !form.severity || !form.description.trim()}
                style={{
                  width: '100%', padding: '10px', fontSize: 13, fontWeight: 600,
                  background: (form.utility_type && form.severity && form.description.trim()) ? '#FF4444' : '#9E9E9E',
                  color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <Send size={14} /> {submitting ? 'Submitting...' : 'Submit Damage Report'}
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setShowForm(true); captureGPS(); }}
              style={{
                width: '100%', padding: '10px', fontSize: 12, fontWeight: 600,
                background: '#FF444410', color: '#FF4444',
                border: `1px dashed #FF444440`, borderRadius: 6, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                marginTop: 6,
              }}
            >
              <AlertTriangle size={14} /> Report Utility Damage
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default DamageReport;
